// File: /pages/SimpleCycles.jsx
import React, { useEffect, useState } from 'react';
import AccordionFolder from '/components/Accordions/AccordionFolder';
import ContentLibrarySidebar from '/components/Layout/ContentLibrarySidebar';
import ContentDisplayTabs from '/components/Tabs/ContentDisplayTabs';
import VideoLinksDisplay from '/components/ContentDisplay/VideoLinksDisplay';
import PerspectiveManagerDisplay from '/components/ContentDisplay/PerspectiveManagerDisplay';
import ClassificationDisplay from '/components/ContentDisplay/ClassificationDisplay';
import AlternativeNamesDisplay from '/components/ContentDisplay/AlternativeNamesDisplay';
import PerspectiveCarousel from '/components/ContentDisplay/PerspectiveCarousel';
import DatabaseImage from '/components/Media/DatabaseImage';
import Link from 'next/link';
import infoPaneStyles from '/components/Panes/InfoPane.module.css';
import sectionStyles from '/components/Sections/Section.module.css';
import tabsStyles from '/components/Tabs/ContentDisplayTabs.module.css';
import displayStyles from '/components/ContentDisplay/ContentDisplay.module.css';
//import styles from './SimpleCycles.module.css';

const CycleContentDisplay = ({ cycle }) => {
  const [activeTab, setActiveTab] = useState('Overview');

  // --- Overview Tab ---
  const overviewTabContent = (
    <div>
      <div className={tabsStyles.overviewContainer}>
        {/* Left Column: Metadata */}
        <div className={displayStyles.leftColumn}>
          <p>
            <strong>Status:</strong> {cycle.status || 'N/A'}
          </p>
          <p>
            <strong>Submission Date:</strong>{' '}
            {cycle.submission_date
              ? new Date(cycle.submission_date).toLocaleDateString()
              : 'N/A'}
          </p>
          {cycle.approval_date && (
            <p>
              <strong>Approval Date:</strong>{' '}
              {new Date(cycle.approval_date).toLocaleDateString()}
            </p>
          )}
          <p>
            <strong>Created At:</strong>{' '}
            {cycle.created_at
              ? new Date(cycle.created_at).toLocaleDateString()
              : 'N/A'}
          </p>
          <p>
            <strong>Updated At:</strong>{' '}
            {cycle.updated_at
              ? new Date(cycle.updated_at).toLocaleDateString()
              : 'N/A'}
          </p>
        </div>
        {/* Right Column: Carousel and DMCA Link */}
        <div className={displayStyles.rightColumn}>
          <PerspectiveCarousel content={cycle} />
          <div className={displayStyles.dmcaLinkContainer}>
            <Link
              href={`/DMCA?contentHash=${cycle.content_id}`}
              className={displayStyles.dmcaLink}
            >
              DMCA Request
            </Link>
          </div>
        </div>
      </div>
      <p className={displayStyles.description}>
        <strong>Description:</strong> {cycle.description || 'No description available'}
      </p>
      <AlternativeNamesDisplay contentId={cycle.content_id} />
    </div>
  );

  // --- Classification Tab ---
  const classificationTabContent = (
    <ClassificationDisplay content={cycle} />
  );

  // --- Media Tab ---
  const galleryImages = Array.isArray(cycle.gallery_images)
    ? cycle.gallery_images
    : [];
  const mediaTabContent = (
    <div>
      <h1>Gallery</h1>
      {galleryImages.length > 0 ? (
        <div className={tabsStyles.galleryContainer}>
          {galleryImages.map((image, index) => (
            <DatabaseImage
              key={index}
              image={image}
              alt={`Gallery Image ${index + 1}`}
              className={tabsStyles.galleryImage}
            />
          ))}
        </div>
      ) : (
        <p>No content available yet.</p>
      )}
      <h1>Perspectives</h1>
      <PerspectiveManagerDisplay content={cycle} />
      <h1>Video Links</h1>
      <VideoLinksDisplay content={cycle} />
    </div>
  );

  return (
    <ContentDisplayTabs
      tabs={[
        { label: 'Overview', content: overviewTabContent },
        { label: 'Media', content: mediaTabContent },
        { label: 'Classification', content: classificationTabContent },
      ]}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    />
  );
};

const SimpleCyclesPage = () => {
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parentCategoryMap, setParentCategoryMap] = useState({});

  // Fetch parent category mapping
  useEffect(() => {
    const fetchParentCategories = async () => {
      try {
        const res = await fetch('/api/categories/categories');
        if (!res.ok) throw new Error('Failed to fetch parent categories');
        const data = await res.json();
        if (data.success && data.categories) {
          const map = {};
          data.categories.forEach((cat) => {
            map[String(cat.id)] = cat.category_name;
          });
          setParentCategoryMap(map);
        }
      } catch (err) {
        console.error('Error fetching parent categories mapping:', err);
      }
    };
    fetchParentCategories();
  }, []);

  // Fetch all content items and filter for type "Simple Cycle"
  useEffect(() => {
    const fetchCycles = async () => {
      try {
        const res = await fetch('/api/content/content');
        if (!res.ok) throw new Error('Failed to fetch simple cycles');
        const data = await res.json();
        let items = Array.isArray(data.content) ? data.content : [];
        items = items.filter(
          (item) =>
            item.type && item.type.trim().toLowerCase() === 'simple cycle'
        );
        setCycles(items);
      } catch (err) {
        console.error('Error fetching simple cycles:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCycles();
  }, []);

  if (loading) return <p>Loading simple cycles...</p>;
  if (error) return <p>Error: {error}</p>;

  // Group cycles by parent_category_id.
  const groups = {};
  cycles.forEach((cycle) => {
    let parentCategories = [];
    if (cycle.parent_category_id) {
      try {
        parentCategories = JSON.parse(cycle.parent_category_id);
        if (!Array.isArray(parentCategories)) {
          parentCategories = [parentCategories];
        }
      } catch (err) {
        parentCategories = cycle.parent_category_id.split(',').map((x) => x.trim());
      }
    }
    if (parentCategories.length === 0) {
      if (!groups['Uncategorized']) groups['Uncategorized'] = [];
      groups['Uncategorized'].push(cycle);
    } else {
      parentCategories.forEach((cat) => {
        const key = String(cat.value || cat);
        if (!groups[key]) groups[key] = [];
        groups[key].push(cycle);
      });
    }
  });

  const groupsArray = Object.keys(groups).map((groupKey) => {
    let groupName = 'Category ' + groupKey;
    if (groupKey === 'Uncategorized') {
      groupName = 'Uncategorized';
    } else if (parentCategoryMap[groupKey]) {
      groupName = parentCategoryMap[groupKey];
    } else if (parentCategoryMap[parseInt(groupKey)]) {
      groupName = parentCategoryMap[parseInt(groupKey)];
    }
    return {
      groupHeader: groupName,
      items: groups[groupKey].map((cycle) => ({
        header: cycle.name,
        content: <CycleContentDisplay cycle={cycle} />,
      })),
    };
  });

  return (
    <section id="simple-cycles" className={`${sectionStyles.section} ${sectionStyles.sectionActive}`}>
      <div className={infoPaneStyles.infoPane}>
        <h1>Shuffle Dance Simple Cycles - Key Components:</h1>
        <h2>
          Explore a curated collection of shuffle dance simple cycles. Click on an accordion header to view the details of each cycle.
        </h2>
      </div>
      <div className={sectionStyles.pageContainer}>
        <div className={sectionStyles.mainContent}>
          {groupsArray.length > 0 ? (
            <AccordionFolder groups={groupsArray} />
          ) : (
            <p>No simple cycles found.</p>
          )}
        </div>
        <ContentLibrarySidebar contentType="Simple Cycle" />
      </div>
    </section>
  );
};

export default SimpleCyclesPage;
